/**
 * Don't ask the API for data of a module the school switched off.
 *
 * Dashboards and shared shells fire module-owned reads in the background
 * (fleet, early warning, hall tickets, learning mission...). When the school
 * has that module off the API answers 403 MODULE_NOT_IN_PLAN — correctly, and
 * uselessly: the request costs a round trip, and worse, a 403 is what makes
 * the app say "This feature isn't enabled for your school". That toast is for
 * a person who just tapped something the plan no longer includes; nobody
 * should be told about a card they were never shown.
 *
 * So `apiGet` asks this first. It short-circuits ONLY when the entitlements
 * we already hold say the module is off, and only when that copy is fresh
 * (fetched this session, within `FRESH_MS`) — a copy remembered from
 * localStorage, or one that has gone stale, never blocks anything. The API
 * stays the enforcement boundary; this is the client declining to knock.
 *
 * Union semantics are inherited from `createModuleAccess`: a PARENT with
 * children in two schools, or an overseer at "All schools", is only blocked
 * when the module is off EVERYWHERE they look.
 */
import { moduleKeyForApiPath } from "@campushubai/shared";
import { queryClient } from "@/lib/query-client";
import { createModuleAccess, type EntitlementsResponse, type ModuleAccess } from "@/lib/module-access";

const ENTITLEMENTS_KEY = ["me", "entitlements"] as const;
export const MODULE_DISABLED_CODE = "MODULE_DISABLED";
/** Freshness window for trusting the held entitlements to decline a request. */
export const GUARD_FRESH_MS = 60_000;

export class ModuleDisabledError extends Error {
  readonly code = MODULE_DISABLED_CODE;
  constructor(
    readonly url: string,
    readonly moduleKey: string,
  ) {
    super(`module "${moduleKey}" is not enabled for this school (${url})`);
    this.name = "ModuleDisabledError";
  }
}

export function isModuleDisabledError(err: unknown): err is ModuleDisabledError {
  return err instanceof ModuleDisabledError || (err as { code?: unknown } | null)?.code === MODULE_DISABLED_CODE;
}

const accessCache = new WeakMap<EntitlementsResponse, ModuleAccess>();

/** The module key an API url belongs to (`/transport/fleet/live?x=1`, with or without `/api/v1`). */
export function moduleKeyForRequestUrl(url: string): string | null {
  if (/^https?:\/\//i.test(url)) {
    try {
      return moduleKeyForApiPath(new URL(url).pathname);
    } catch {
      return null;
    }
  }
  return moduleKeyForApiPath(url);
}

/** The module this request would hit, IF the held entitlements say it is off. */
export function disabledModuleFor(url: string, now: number = Date.now()): string | null {
  const key = moduleKeyForRequestUrl(url);
  if (!key) return null;
  const state = queryClient.getQueryState<EntitlementsResponse>(ENTITLEMENTS_KEY);
  const data = state?.data;
  if (!data || !state || now - state.dataUpdatedAt > GUARD_FRESH_MS) return null;
  let access = accessCache.get(data);
  if (!access) {
    access = createModuleAccess(data);
    accessCache.set(data, access);
  }
  return access.hasModule(key) ? null : key;
}

/** Throws `ModuleDisabledError` instead of calling the API for an off module. */
export function assertModuleEnabledForUrl(url: string): void {
  const key = disabledModuleFor(url);
  if (key) throw new ModuleDisabledError(url, key);
}
